import type { IntegrationRuntimeSummary, IntegrationRuntimeStatus } from './integration'
import type { BudgetEntityRef } from './budget'
import type { MemberControls, MemberControlsSettings, MemberAiDailyBudgetCounter } from './memberControls'

export type CanvasElementType = 'GOAL' | 'TASK' | 'SUBTASK'
export type UserRole = 'OWNER' | 'ADMIN' | 'EDITOR' | 'VIEWER' | 'READONLY' | 'HELPER' | 'LEARNER'
export type AccountRole = 'MEMBER' | 'HELPER'
export type DisplayPreferenceType = 'FIRST_LAST' | 'USERNAME' | 'PHONE'
export type TimelineBoardKey = 'VISIONBOARD' | 'DASHBOARD' | 'ARCHIVE' | 'BIN'

export const TIMELINE_BOARD_KEYS: TimelineBoardKey[] = ['VISIONBOARD', 'DASHBOARD', 'ARCHIVE', 'BIN']

export const TIMELINE_BOARD_LABELS: Record<TimelineBoardKey, string> = {
  VISIONBOARD: 'Visionboard',
  DASHBOARD: 'Dashboard',
  ARCHIVE: 'Archive',
  BIN: 'Bin',
}

/**
 * Kind of node that lives in the `checklist_items` collection. Default is the
 * regular CHECKLIST item; GATE_AND / GATE_OR are pure logic-gate nodes drawn
 * on the canvas (2-4 inputs, 1 output). Gates participate in prerequisite
 * resolution: a downstream checklist item that has a gate as its prerequisite
 * unlocks when the gate's inputs satisfy AND-all / OR-any semantics.
 */
export type ChecklistNodeKind = 'CHECKLIST' | 'GATE_AND' | 'GATE_OR'

/**
 * The user-driven workflow status shown in the node detail header dropdown.
 * Distinct from the date/time-derived lifecycle chip (Ready/Active/Over).
 * Only `COMPLETED` is gated (proof file, location check-in, no open blocker).
 */
export type ChecklistWorkflowStatus =
  | 'CREATED'
  | 'ASSIGNED'
  | 'ASSESSING'
  | 'ACCEPTED'
  | 'ONGOING'
  | 'COMPLETED'
  | 'NOT_REQUIRED'
  | 'POSTPONED'

export type TimelineWorkflowStatus = 'TODO' | 'IN_PROGRESS' | 'COMPLETE'
export type BoardStatus = TimelineWorkflowStatus

/** How a node's calendar window is expressed. */
export type ChecklistScheduleMode = 'DATED' | 'DURATION'

/**
 * Time-derived lifecycle status, computed from `scheduleStartDate` /
 * `scheduleEndDate` + workspace `todosLeadDays` at read time. Distinct from
 * `workflowStatus` (user-driven). Semantics:
 *   DURATION (both dates): upcoming → active (start-leadDays..end) → archived
 *   DATED   (start only) : active (until start passes) → archived
 *   no dates            : always 'active' (backward compatible)
 */
export type ChecklistTimeStatus = 'upcoming' | 'active' | 'archived'

/**
 * A directional dependency link from a checklist node to a canvas element
 * (Goal/Task/Subtask). Used by both "is blocked by" and "is a blocker for".
 * The title is snapshotted so the chip still reads if the target is removed.
 */
export interface ChecklistNodeLink {
  targetId: string
  targetType: 'CHECKLIST'
  title: string
}
export type DashboardViewKey =
  | 'TIMELINE'
  | 'CONSTELLATION'
  | 'RADAR'
  | 'SANDFALL'
  | 'JOURNEY'
  | 'GRID'
  // ── Track 6 specialty views — scaffolded ahead of their renderers.
  // GRID and HEATMAP ship as separate dashboard views; the later specialty
  // views are intentionally declared early so the workspace doc schema and
  // the view-config normalizer (in WorkspaceContext) accept the tokens without
  // churn. They're inert until their renderers land in later phases.
  | 'HEATMAP'
  | 'STRIPBOARD'
  | 'WARROOM'
  | 'LEADERBOARD'
  | 'PERIMETER'
  | 'LOOTDROP'
  | 'FLOWBAND'
  | 'BURNSHORE'
  | 'CADENCE'

export const DASHBOARD_VIEW_KEYS: DashboardViewKey[] = [
  'TIMELINE',
  'CONSTELLATION',
  'RADAR',
  'SANDFALL',
  'JOURNEY',
  'GRID',
  'HEATMAP',
  'STRIPBOARD',
  'WARROOM',
  'LEADERBOARD',
  'PERIMETER',
  'LOOTDROP',
  'FLOWBAND',
  'BURNSHORE',
  'CADENCE',
]

/**
 * Views that are scaffolded (types/files/registration) but not yet shipped
 * for end-user toggling. Settings UI renders them disabled with a "Coming
 * soon" tooltip; the workspace-config seeder and the DashboardContainer
 * renderable-views computation both filter them out so they never appear
 * even if a prior config persisted them as enabled.
 */
export const COMING_SOON_VIEW_KEYS: ReadonlySet<DashboardViewKey> = new Set([
])

/** Subset of `DASHBOARD_VIEW_KEYS` safe for default seeding + render output. */
export const AVAILABLE_VIEW_KEYS: DashboardViewKey[] = DASHBOARD_VIEW_KEYS.filter(
  (key) => !COMING_SOON_VIEW_KEYS.has(key) && key !== 'LEADERBOARD',
)

export type AppTheme =
  | 'MOSAIC_LIGHT'
  | 'MOSAIC_DARK'
  | 'LIGHT'
  | 'DARK'
  | 'OLED_BLACK'
  | 'MIDNIGHT'
  | 'AURORA'
  | 'EMBER'
  | 'CELESTIAL'
  | 'PEARL'
  | 'LINEN'
  | 'GLACIER'
  | 'BLUSH'
  | 'SAGE'

export type CanvasBackgroundKey =
  | 'DEFAULT'
  | 'BLUEPRINT_GRID'
  | 'NEBULA'
  | 'CIRCUIT'

export type FocusSoundscapeKey =
  | 'SILENCE'
  | 'LOFI_RAIN'
  | 'ALPHA_WAVE'

export type DigestCadence = 'DAILY' | 'WEEKLY'

export interface QuietHoursWindow {
  /** Local-hour start [0, 23]. */
  startHour: number
  /** Local-hour end [0, 23]. Window wraps midnight when endHour <= startHour. */
  endHour: number
  /** IANA timezone the window is expressed in, e.g. "America/Los_Angeles". */
  timezone: string
}

export type OrbitSkinKey = 'cream' | 'ivory' | 'glass' | 'ink' | 'outline'

export interface ClientUserPreferences {
  theme: AppTheme
  themeMode?: 'FIXED' | 'SYSTEM'
  memberAppTheme: AppTheme | null
  animationProfile: 'FLUID' | 'FAST' | 'REDUCED_MOTION'
  statusMarkers: 'SHORT' | 'LONG'
  loadingStyle: 'CONSTELLATION' | 'STARFIELD'
  canvasBackground: CanvasBackgroundKey
  focusSoundscape: FocusSoundscapeKey
  orbitSkin?: OrbitSkinKey
  soundEnabled: boolean
  hapticEnabled: boolean
  highFidelityEffectsEnabled: boolean
  watchdogLeaseTimeoutMinutes: number
  railCollapsedShowLabels: boolean
  railAutoPopupOnHover: boolean
  aiMagicRequireQuickAnswerSubmit: boolean
  aiMagicDraftReadyNotifications: boolean
  aiMagicAutoOpenManageDrafts: boolean
  enableCelebration: boolean
  enableDebugMode: boolean
  supportTicketLastSeenAt: string
  collapsingHeaderEnabled: boolean
  digestCadence: DigestCadence
  quietHours: QuietHoursWindow | null
  lookVersion?: number
  mosaicToastPending?: boolean
}

export type CalendarStyleId = 'cards' | 'glass'
export type CalendarRangeMode = 'day' | 'week' | 'month'
export type CalendarBoardScopeMode = 'current' | 'custom'

export interface CalendarBoardFilterState {
  /** Map of element-or-checklist id → explicit user toggle.
   * When absent, the effective visibility falls back to the parent's cascade. */
  explicit: Record<string, boolean>
  /** Master toggle used by "Show all" / "Hide all". When false, items default to hidden
   * unless explicitly toggled true; when true, items default visible unless explicitly false. */
  showAll: boolean
}

export interface CalendarPreferences {
  styleId: CalendarStyleId
  lastRange: CalendarRangeMode
  boardScope: {
    mode: CalendarBoardScopeMode
    boardKeys?: TimelineBoardKey[]
  }
  /** Per-board filter state keyed by `TimelineBoardKey`. */
  filterState?: Partial<Record<TimelineBoardKey, CalendarBoardFilterState>>
  /** Class sessions mode: MINE (default), ALL (unrestricted EDITOR+), or OFF. */
  eduSessionsMode?: 'MINE' | 'ALL' | 'OFF'
}

export type WorkspaceLimitKey =
  | 'maxGoalsPerBoard'
  | 'maxTasksPerBoard'
  | 'maxTasksPerGoal'
  | 'maxSubtasksPerBoard'
  | 'maxSubtasksPerTask'
  | 'maxChecklistsPerBoard'
  | 'maxChecklistsPerContainer'
  | 'maxWorkspaceMembers'
  | 'maxPendingInvites'
  | 'maxEnabledDashboardViews'
  | 'maxAiPromptCharacters'
  | 'titleMaxLength'
  | 'commentMaxLength'
  | 'searchMaxLength'
  | 'autoSpawnTitleMaxLength'
  | 'weightageMax'
  | 'estimatedHoursMax'
  | 'actualCostMax'
  | 'countdownHoursMax'
  | 'delayHoursMax'
  | 'autoSpawnYOffsetMax'

export interface WorkspaceLimits {
  hardCaps: Record<WorkspaceLimitKey, number>
  softCaps: Record<WorkspaceLimitKey, number>
  sameForAllBoards: boolean
  boardSoftCaps?: Partial<Record<TimelineBoardKey, Partial<Record<WorkspaceLimitKey, number>>>>
  input: {
    titleMaxLength?: number
    commentMaxLength?: number
    searchMaxLength?: number
    autoSpawnTitleMaxLength?: number
    weightageMax?: number
    estimatedHoursMax?: number
    actualCostMax?: number
    countdownHoursMax?: number
    delayHoursMax?: number
    autoSpawnYOffsetMax?: number
  }
}

export interface WorkspaceRecord {
  id: string
  name: string
  slug?: string
  ownerId: string
  /** IANA timezone used to interpret workspace-local checklist dates/times. */
  timezone?: string
  kind?: 'PERSONAL' | 'TEAM'
  credits?: number
  plan?: string
  teamBalanceCredits?: number
  inviteToken?: string
  isPersonal?: boolean
  isPublic?: boolean
  deletion?: {
    status?: 'PENDING' | 'DELETING'
    requestedAt?: unknown
    purgeAfter?: unknown
    requestedBy?: string
    wasPublic?: boolean
  }
  checklistInputLimits?: {
    titleMaxLength?: number
    commentMaxLength?: number
    searchMaxLength?: number
    autoSpawnTitleMaxLength?: number
    weightageMax?: number
    estimatedHoursMax?: number
    actualCostMax?: number
    countdownHoursMax?: number
    delayHoursMax?: number
    autoSpawnYOffsetMax?: number
  }
  workspaceLimits?: WorkspaceLimits
  enabledDashboardViews?: DashboardViewKey[]
  defaultDashboardView?: DashboardViewKey
  onboardingSetup?: {
    version?: number
    roleTitle?: string | null
    useCase?: string | null
    teamSize?: string | null
    audience?: 'SOLO' | 'SOLO_HELPERS' | 'TEAM' | null
    intentText?: string | null
    focusAreas?: string[]
    preferences?: {
      theme?: string
      /** @deprecated Density is removed in onboarding (Plan OC2). Kept for legacy reads. */
      density?: string
      aiSuggestionsEnabled?: boolean
      notificationsEnabled?: boolean
      gamificationEnabled?: boolean
    }
    completedAt?: unknown
  }
  workspacePreferences?: {
    density?: string
    aiSuggestionsEnabled?: boolean
    notificationsEnabled?: boolean
    gamificationEnabled?: boolean
    cardBackgroundsEnabled?: boolean
    checklistBlockedTodoPolicy?: {
      allowCompletion?: boolean
      allowProof?: boolean
      allowStatusChange?: boolean
      allowComment?: boolean
    }
  }
  cardBackgroundSettings?: {
    enabled?: boolean
  }
  createdAt?: unknown
}

export type StorageFileVisibility = 'PRIVATE' | 'WORKSPACE_SHARED'
export type StorageFileSource = 'LOCAL' | 'GOOGLE_DRIVE'
export type StorageFileStatus = 'ACTIVE' | 'FAILED' | 'UNAVAILABLE' | 'REMOTE_TRASHED' | 'REMOTE_GONE'
export type StorageConnectionProvider = 'google_drive' | 'dropbox' | 'onedrive'

export type StorageTier = 'FREE' | 'PRO' | 'TEAM' | 'ENTERPRISE'

export type ArtifactOrigin = 'LOCAL' | 'DRIVE' | 'DROPBOX' | 'ONEDRIVE' | 'S3'
export type ArtifactDocumentType = 'pdf' | 'img' | 'xls' | 'doc' | 'video' | 'audio' | 'other'

export interface ScheduledUploadBatch {
  id: string
  workspaceId: string
  uploaderId: string
  uploaderName: string
  label: string
  fileNames: string[]
  totalSizeBytes: number
  origin: ArtifactOrigin
  scheduledFor: unknown
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'CANCELLED' | 'FAILED'
  metadata?: Record<string, unknown>
  createdAt?: unknown
  updatedAt?: unknown
}

export type UploadHubLedgerAction =
  | 'FILE_UPLOADED'
  | 'BATCH_STARTED'
  | 'BATCH_SCHEDULED'
  | 'BATCH_CANCELLED'
  | 'ARTIFACT_DELETED'
  | 'ARTIFACT_RETAGGED'
  | 'SYNC_TRIGGERED'
export type IntegrationProvider = 'NOTION' | 'JIRA' | 'GOOGLE_CALENDAR' | 'GOOGLE_SHEETS' | 'ZOOM' | 'GITHUB' | 'FIGMA' | 'WEBEX' | 'GOOGLE_MEET'
export type IntegrationComingSoonProvider = 'SLACK' | 'GITHUB' | 'OBSIDIAN'
export type IntegrationConnectionStatus = 'ACTIVE' | 'ACTION_REQUIRED' | 'DISCONNECTED' | 'DORMANT' | 'SYNCING' | 'ERROR'
export type IntegrationSyncStatus = 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CONFLICT' | 'SKIPPED'

export interface IntegrationTargetScope {
  boards: TimelineBoardKey[]
  elementTypes: CanvasElementType[]
}

export interface IntegrationTarget {
  id: string
  externalId: string
  displayName: string
  scope: IntegrationTargetScope
  config: Record<string, string | boolean | null>
  createdAt?: unknown
  updatedAt?: unknown
}

export interface IntegrationProviderTargetMapping {
  externalId: string
  targetId: string
  providerContainerId?: string | null
  providerUpdatedAt?: string | null
  lastSyncedAt?: unknown
  lastLocalCompleted?: boolean
  conflictId?: string | null
}

export interface IntegrationProviderMapping {
  provider: IntegrationProvider
  targets: Record<string, IntegrationProviderTargetMapping>
}

export type ElementProviderMappings = Record<string, IntegrationProviderMapping>

export interface WorkspaceStorageQuota {
  usedBytes: number
  reservedBytes: number
  baseLimitBytes: number
  burstBytes: number
  totalBytes: number
  burstExpiresAt?: unknown
  isAllowed: boolean
}

export interface WorkspaceFileMetadata {
  id: string
  workspaceId: string
  fileName: string
  source: StorageFileSource
  status: StorageFileStatus
  visibility: StorageFileVisibility
  sizeBytes: number
  mimeType: string
  checksumSha256?: string | null
  uploaderId: string
  uploaderName: string
  storagePath?: string | null
  providerPath?: string | null
  providerFileId?: string | null
  connectionId?: string | null
  artifactType: string
  artifactId?: string | null
  version: number
  parentFileId?: string | null
  duplicateChecksum?: boolean
  createdAt?: unknown
  syncTimestamp?: unknown
  origin?: ArtifactOrigin
  documentType?: ArtifactDocumentType
  projectId?: string | null
  aiTags?: string[]
  sourceChatId?: string | null
  sourceChatTitle?: string | null
  originalAttachmentType?: string | null
  /** Provider linkage is server-owned; a missing mirror means this is local-only. */
  driveMirror?: {
    connectionId: string
    folderId: string
    fileId: string
    webViewLink?: string | null
    status: 'ACTIVE' | 'DORMANT' | 'REMOTE_TRASHED' | 'REMOTE_GONE'
    lastSyncedAt?: unknown
  } | null
}

export interface StorageConnectionMetadata {
  id: string
  workspaceId: string
  creatorId: string
  creatorName: string
  provider: StorageConnectionProvider | IntegrationProvider
  displayName: string
  status: 'ACTIVE' | 'DORMANT' | 'REVOKED' | 'ERROR' | IntegrationConnectionStatus
  folderId?: string | null
  driveScopeTier?: 'METADATA' | 'FILE_ACCESS'
  driveScopeUpgradedAt?: unknown
  targets?: IntegrationTarget[]
  lastErrorCode?: string | null
  lastErrorAt?: unknown
  updatedAt?: unknown
  disconnectedAt?: unknown
  disconnectedById?: string | null
  createdAt?: unknown
  lastSyncAt?: unknown
  runtime?: IntegrationRuntimeSummary
  runtimeStatus?: IntegrationRuntimeStatus
  runtimeSummary?: string
  runtimeUpdatedAt?: unknown
  webhookSubscriptions?: Array<{
    subscriptionId: string
    resourceId?: string
    scope: string
    events: string[]
    signingAlgorithm: 'HMAC_SHA256' | 'JIRA_QUERY_STRING' | 'NOTION_V1' | 'GOOGLE_CHANNEL'
    createdAt: unknown
    expiresAt?: unknown
    lastPingAt?: unknown
  }>
  webhookSecretRef?: { exists: true; updatedAt: unknown }
  /** Server-owned Drive watches; per-channel ciphertext lives in storage_secrets. */
  driveWatchChannels?: Array<{
    channelId: string
    resourceId: string
    resourceUri: string
    watchedScope: { kind: 'FILE' | 'FOLDER'; driveFileId: string }
    expirationMs: number
    startPageToken: string
    lastProcessedPageToken?: string
    createdAt: unknown
    lastRenewedAt?: unknown
  }>
}

export interface IntegrationSyncJob {
  id: string
  workspaceId: string
  connectionId: string
  targetId: string
  elementId: string
  provider: IntegrationProvider
  trigger: 'MANUAL' | 'SCHEDULED' | 'PARENT_REPAIR'
  intervalKey: string
  status: IntegrationSyncStatus
  attemptCount: number
  createdAt?: unknown
  startedAt?: unknown
  completedAt?: unknown
  expiresAt?: unknown
  errorCode?: string | null
}

export interface WorkspaceSyncActivity {
  id: string
  workspaceId: string
  connectionId: string
  targetId?: string | null
  elementId?: string | null
  provider: IntegrationProvider
  status: IntegrationSyncStatus
  action: string
  summary: string
  errorCode?: string | null
  actorId?: string | null
  createdAt?: unknown
  expiresAt?: unknown
}

export interface IntegrationConflict {
  id: string
  workspaceId: string
  connectionId: string
  targetId: string
  elementId: string
  provider: IntegrationProvider
  externalId: string
  providerUpdatedAt: string
  localBaselineUpdatedAt?: string | null
  status: 'OPEN' | 'OVERWRITTEN' | 'ACCEPTED_PROVIDER' | 'DISCONNECTED_MAPPING'
  createdAt?: unknown
  resolvedAt?: unknown
  resolvedById?: string | null
  expiresAt?: unknown
}

export interface TombstoneMapping {
  id: string
  workspaceId: string
  connectionId: string
  targetId: string
  localId: string
  externalId: string
  provider: IntegrationProvider
  createdAt?: unknown
  expiresAt?: unknown
}

export interface QueuedStorageUpload {
  id: string
  workspaceId: string
  uploaderId: string
  uploaderName: string
  fileName: string
  mimeType: string
  sizeBytes: number
  checksumSha256: string
  visibility: StorageFileVisibility
  status: 'RESERVED' | 'SCHEDULED' | 'ACTIVE' | 'CANCELLED' | 'EXPIRED' | 'FAILED'
  scheduledAt?: unknown
  createdAt?: unknown
}

/** The level of access a restricted member has been granted on a specific node. */
export type NodeGrant = 'VIEW' | 'EDIT'

/**
 * Per-member scoped access configuration.
 * mode 'ALL'  → member is unrestricted (today's behaviour); grants are ignored.
 * mode 'RESTRICTED' → member only accesses nodes they have a grant on (or an ancestor does).
 * A missing accessScope field is treated as mode 'ALL'.
 */
export interface AccessScope {
  mode: 'ALL' | 'RESTRICTED'
  /** nodeId → NodeGrant.  Only present in mode 'RESTRICTED'. */
  grants: Record<string, NodeGrant>
}

export interface WorkspaceMember {
  id: string
  workspaceId: string
  workspaceSlug?: string
  uid?: string
  userId?: string
  user_email?: string
  email?: string
  role: UserRole
  boardRoles?: Partial<Record<TimelineBoardKey, UserRole>>
  /** Scoped access configuration.  Missing means 'ALL' (unrestricted). */
  accessScope?: AccessScope
  /** Per-person controls and profile assignment (R21 TT3.a). */
  controls?: MemberControlsSettings
  /** Computed effective controls written by server. */
  effectiveControls?: MemberControls
  /** Daily AI budget counter tracked in workspace timezone. */
  aiBudgetCounter?: MemberAiDailyBudgetCounter
  credits?: number
  dailyCreditAllowance?: number
  creditsConsumedToday?: number
  helperAppLocale?: string | null
  helperAppLocaleUpdatedAt?: unknown
  helperAppLocaleUpdatedBy?: string | null
  canDelegateFieldPass?: boolean
  canDelegateFieldPassUpdatedAt?: unknown
  canDelegateFieldPassUpdatedBy?: string | null
  defaultDashboardView?: DashboardViewKey
  status?: 'ACTIVE' | 'PENDING' | 'DECLINED' | 'REVOKED'
  invitedAt?: unknown
  respondedAt?: unknown
  declinedAt?: unknown
  revokedAt?: unknown
  lastInviteSentAt?: unknown
  inviteStatusHistory?: Array<{ status: string; at: unknown; by?: string }>
}

export type NotificationPriority = 'HIGH' | 'MEDIUM' | 'LOW'
export type NotificationChannel = 'IN_APP' | 'PUSH' | 'EMAIL' | 'WHATSAPP'

// Overhaul tiering: HIGH = push immediately, LOW = queue into digest.
export type NotificationTier = 'HIGH' | 'LOW'
export type NotificationCategory =
  | 'MENTIONS'
  | 'DUE'
  | 'AI_FAILS'
  | 'SUBSCRIPTION'
  | 'SECURITY'
  | 'GROUP_CHAT'
  | 'GAMIFICATION'
  | 'TIPS'
  | 'UPDATES'
  | 'INGESTION'
  | 'AI_JOBS'
  | 'DIGEST'
  | 'EDU_ATTENDANCE'
  | 'EDU_LEAVE'
  | 'EDU_CLASSES'
  | 'EDU_HOMEWORK'
  | 'EDU_TODAY'
  | 'EDU_REPORTS'
  | 'EDU_FEES'
  | 'EDU_BOOKINGS'
  | 'EDU_PROGRESS'
  | 'EDU_ANNOUNCEMENTS'
  | 'EDU_MESSAGES'
  | 'EDU_ADMISSIONS'
  | 'EDU_EVENTS'
export type NotificationWorkspaceAudience = 'OWNER' | 'ADMIN' | 'EDITOR' | 'VIEWER' | 'READONLY' | 'ALL_MEMBERS'
export type NotificationEntityType =
  | CanvasElementType
  | 'CHECKLIST_ITEM'
  | 'WORKSPACE'
  | 'MEMBER'
  | 'INVITE'
  | 'CREDIT_LEDGER'
  | 'RAID'
  | 'REPORT'
  | 'ACHIEVEMENT'
  | 'SYSTEM'
  | 'FORM'
  | 'FORM_RESPONSE'
  | 'EDU_LEARNER_DAY'
  | 'STOCK'
  | 'STOCK_ITEM'
  | 'QUOTE'

export type NotificationEventType =
  | 'MENTION_CREATED'
  | 'ASSIGNMENT_CREATED'
  | 'ASSIGNMENT_CHANGED'
  | 'DUE_SOON'
  | 'DUE_TODAY'
  | 'CHECKLIST_DUE'
  | 'OVERDUE'
  | 'MISSED'
  | 'CHECKLIST_MISSED'
  | 'BLOCKED'
  | 'UNBLOCKED'
  | 'WORKFLOW_STATUS_CHANGED'
  | 'RELOCKED'
  | 'PROOF_SUBMITTED'
  | 'PROOF_APPROVED'
  | 'PROOF_REJECTED'
  | 'EXPENSE_APPROVED'
  | 'EXPENSE_REJECTED'
  | 'EDU_ANNOUNCEMENT_WHATSAPP'
  | 'WINDOW_ENDING'
  | 'CHECKLIST_WINDOW_ENDING'
  | 'CHECKLIST_SUMMARY'
  | 'COMPLETION_REVIEW_REQUIRED'
  | 'RAID_STARTED'
  | 'RAID_ENRAGE_WARNING'
  | 'RAID_COMPLETED'
  | 'RAID_FAILED'
  | 'CREDITS_LOW'
  | 'CREDITS_SPENT'
  | 'CREDITS_REFUNDED'
  | 'AD_LOCK_READY'
  | 'STREAK_WARNING'
  | 'ACHIEVEMENT_UNLOCKED'
  | 'COSMETIC_UNLOCKED'
  | 'INVITE_RECEIVED'
  | 'MEMBER_ROLE_CHANGED'
  | 'BOARD_ROLE_CHANGED'
  | 'REPORT_READY'
  | 'REPORT_FAILED'
  | 'SAVE_FAILED'
  | 'PERMISSION_CHANGED'
  | 'SYSTEM_NOTICE'
  | 'FORM_REQUESTED'
  | 'STOCK_INSUFFICIENT'
  | 'STOCK_SUGGESTION_WAITING'
  | 'STOCK_TRANSFER_ARRIVED'
  | 'STOCK_EXPIRING'
  | 'STOCK_LOW'
  | 'HELPER_BLOCKER_RAISED'
  | 'CLIENT_QUOTE_DECIDED'
  | 'BUDGET_OVER_LIMIT'
  | 'DIGEST'
  | 'EDU_ABSENCE'
  | 'EDU_LATE'
  | 'EDU_LEAVE_REQUESTED'
  | 'EDU_LEAVE_DECIDED'
  | 'EDU_HOMEWORK_SET'
  | 'EDU_HOMEWORK_SUBMITTED'
  | 'EDU_HOMEWORK_RETURNED'
  | 'EDU_HOMEWORK_APPROVED'
  | 'EDU_HOMEWORK_DUE'
  | 'EDU_CLASS_CANCELLED'
  | 'EDU_CLASS_SUBSTITUTE'
  | 'EDU_TODAY_SUMMARY'
  | 'EDU_REPORT_CARD_PUBLISHED'
  | 'EDU_FEE_DUE_SOON'
  | 'EDU_FEE_DUE_TODAY'
  | 'EDU_FEE_OVERDUE'
  | 'EDU_FEE_RECEIPT'
  | 'EDU_BOOKING_STAFF_BOOKED'
  | 'EDU_BOOKING_PROMOTED'
  | 'EDU_BOOKING_LATE_CANCEL_USED'
  | 'EDU_BOOKING_NO_SHOW_USED'
  | 'EDU_BOOKING_BLOCKED'
  | 'EDU_BOOKING_PACK_EXPIRING'
  | 'EDU_BOOKING_PACK_EXPIRED'
  | 'EDU_BOOKING_MEMBERSHIP_FROZEN'
  | 'EDU_BOOKING_SESSION_CANCELLED'
  | 'EDU_CERT_ISSUED'
  | 'EDU_CERT_EXPIRING'
  | 'EDU_PRACTICE_COMMENT'
  | 'EDU_ANNOUNCEMENT_POSTED'
  | 'EDU_FEED_POSTED'
  | 'EDU_MESSAGE_RECEIVED'
  | 'EDU_ENQUIRY_NEW'
  | 'EDU_ENQUIRY_ASSIGNED'
  | 'EDU_ENQUIRY_FOLLOW_UP_DUE'
  | 'EDU_EVENT_PUBLISHED'
  | 'EDU_EVENT_UPDATED'
  | 'EDU_EVENT_CANCELLED'
  | 'EDU_EVENT_REMINDER'
  | 'EDU_PTM_BOOKED'
  | 'EDU_PTM_RESCHEDULED'
  | 'EDU_PTM_CANCELLED'
  | 'EDU_PTM_REMINDER'
  | 'EDU_PTM_SUMMARY_PUBLISHED'


export interface NotificationRecord {
  id: string
  recipientId: string
  actorId?: string | null
  workspaceId?: string | null
  workspaceName?: string | null
  workspaceAudience?: NotificationWorkspaceAudience | null
  eventType: NotificationEventType
  entityType: NotificationEntityType
  entityId?: string | null
  entityTitle?: string | null
  priority: NotificationPriority
  channels: NotificationChannel[]
  title: string
  body: string
  guardianPersonaId?: string | null
  deepLink?: string | null
  deeplink?: string | null
  workspaceDeepLink?: string | null
  batchKey?: string | null
  dedupeKey?: string | null
  tier?: NotificationTier
  category?: NotificationCategory
  isDigest?: boolean
  digestCount?: number
  digestItems?: Array<{ title: string; body: string; deepLink?: string | null; workspaceDeepLink?: string | null; category?: NotificationCategory; workspaceId?: string | null }>
  readAt?: unknown
  dismissedAt?: unknown
  clearedAt?: unknown
  createdAt?: unknown
  expiresAt?: unknown
  delivery?: {
    push?: {
      status: 'PENDING' | 'SENT' | 'PARTIAL' | 'FAILED' | 'SKIPPED'
      attemptedAt?: unknown
      successCount?: number
      failureCount?: number
      error?: string
    }
  }
  data?: Record<string, unknown>
}

export interface NotificationPreferenceMatrix {
  inApp: boolean
  push: boolean
  email: boolean
  digest: boolean
}

export type NotificationCategoryToggles = Record<NotificationCategory, NotificationPreferenceMatrix>

export interface QuietModePreferences {
  enabled: boolean
  startHour: number
  endHour: number
}

export interface UserNotificationPreferences {
  userId: string
  timezone: string
  categories: NotificationCategoryToggles
  digest: { enabled: boolean }
  quietMode: QuietModePreferences
  bundleRepeats?: boolean
  createdAt?: unknown
  updatedAt?: unknown
}

// Phase 2 persistence contract. Phase 1 stores channel intent in the unified
// settings document but performs no email delivery.
export interface UserEmailPreferences {
  userId: string
  categories: Partial<Record<NotificationCategory, boolean>>
  digestEnabled: boolean
  transactionalEnabled: true
  updatedAt?: unknown
}

export interface WorkspaceNotificationPreferences {
  workspaceId: string
  audience: NotificationWorkspaceAudience
  masterEnabled: boolean
  defaults: {
    blockers: NotificationPreferenceMatrix
    mentions: NotificationPreferenceMatrix
    dueDates: NotificationPreferenceMatrix
    raids: NotificationPreferenceMatrix
    credits: NotificationPreferenceMatrix
    reports: NotificationPreferenceMatrix
    permissions: NotificationPreferenceMatrix
    activityDigest: NotificationPreferenceMatrix
  }
  batching: {
    enabled: boolean
    nonUrgentDelayMinutes: number
    editAggregationWindowSeconds: number
  }
  createdAt?: unknown
  updatedAt?: unknown
}

export interface DocumentLock {
  id: string
  workspaceId: string
  lockedBy: string
  lockedByName: string
  expiresAt: unknown
}

export interface UserProfile {
  uid: string
  email: string
  role?: AccountRole
  firstName?: string | null
  lastName?: string | null
  username?: string | null
  phone?: string | null
  address?: string | null
  educationNotes?: string | null
  displayName?: string | null
  photoURL?: string | null
  profileImage?: {
    source?: 'GOOGLE' | 'AVATAR' | null
    avatarId?: string | null
    updatedAt?: unknown
  } | null
  displayPreferences?: {
    type?: DisplayPreferenceType | null
    computedName?: string
  }
  helperMetadata?: {
    internalUid: string
    mobileNumber: string
  } | null
  metadata?: {
    onboardingCompleted?: boolean
    [key: string]: unknown
  }
  credits: number
  hasSetFirstGoal: boolean
  dailyCratesClaimed: boolean
  dailyTasksCompletedCount: number
  referralCount: number
  referredBy: string | null
  deletion?: {
    status?: 'PENDING' | 'DELETING'
    requestedAt?: unknown
    purgeAfter?: unknown
    requestedBy?: string
  }
  consent?: UserConsent
}

export interface UserConsent {
  termsVersion: string
  privacyVersion: string
  acceptedAt: unknown
}

export interface TeammateCursorPresence {
  userId: string
  userName: string
  userColor: string
  canvasX: number
  canvasY: number
  activeFocusedNodeId: string | null
  lastUpdated: string
}

export interface ChecklistItemVerification {
  requiresProofFile?: boolean
  proofFileUrl: string | null
  proofFileName: string | null
  requiresCoSign: boolean
  coSignerRoleTarget: 'ADMIN' | 'MANAGER' | string
  coSignedByUid: string | null
  coSignedByName: string | null
  isPendingReview: boolean
  requiresLocationPin: boolean
  targetCoordinates: { lat: number; lng: number } | null
  verifiedCoordinates: { lat: number; lng: number } | null
  proofVideoUrl: string | null
}

export interface ChecklistItemDependencies {
  blockedByExternalId?: string | null
  interWorkspaceTargetId?: string | null
  dependencyLogicGate?: 'ALL_PREREQUISITES_MET' | 'ANY_PREREQUISITE_MET'
  hasAutoSpawnTrigger?: boolean
  autoSpawnTemplates?: Array<{
    title: string
    weightage: number
    relativeYOffset: number
  }>
}

export interface ChecklistComment {
  id: string
  pageKey?: string | null
  userId: string
  userName: string
  userAvatarUrl?: string
  text: string
  timestamp: string
  parentCommentId?: string | null
  mentionedUserIds?: string[]
  mentionedUsers?: Array<{ userId: string; displayName: string }>
  reactions?: Record<string, string[]>
  editedAt?: string | null
  deletedAt?: string | null
  deletedBy?: string | null
  attachments?: ChecklistAttachment[]
  attachmentCount?: number
}

export interface ChecklistAttachment {
  id: string
  proofSubmissionId?: string
  name: string
  contentType: string
  size: number
  downloadUrl: string
  storagePath: string
  uploadedById: string
  uploadedByName: string
  uploadedAt: string
}

export type ChecklistProofKind = 'PROOF' | 'MEMO'

export interface ChecklistProofSubmission extends ChecklistAttachment {
  pageKey?: string | null
  checklistItemId: string
  workspaceId: string
  kind: ChecklistProofKind
}

export interface ChecklistMemberOption {
  userId: string
  displayName: string
  email?: string
  role: UserRole
}

export interface ChecklistItemAdvancedContext {
  countdownHours?: number | null
  timerStartedAt?: string | null
  timerExpiredAt?: string | null
  isUrgentCrisisMode?: boolean
  enforcePaintSpringDelay?: boolean
  delayDurationHours?: number
  commentsCount?: number
  voiceMemoBlobUrl?: string | null
  isDescriptionPrivate?: boolean
}

export interface ChecklistItemOperationalMetrics {
  estimatedHours?: number | null
  actualMinutes?: number | null
  actualCost?: number | null
  complexityStars?: number
  budgetLimit?: number | null
  actualSpend?: number
  estimatedCost?: number | null
}

export interface CanvasElement {
  id: string
  workspaceId: string
  workspaceSlug?: string
  type: CanvasElementType
  parentId: string | null
  board?: TimelineBoardKey
  publicId?: string
  routePath?: string
  parentRoutePath?: string
  deletedFromBoard?: TimelineBoardKey | null
  deletedAt?: unknown
  title: string
  description: string
  progress: number
  boardStatus: BoardStatus | null
  /** Legacy pre-Delta Stripboard field. Read-only compatibility only. */
  workflowStatus?: TimelineWorkflowStatus | null
  priority: number
  dependencyNodeIds: string[]
  invalidDependencyNodeIds?: string[]
  startDate?: string | null
  endDate?: string | null
  recordVersion: number
  statusSyncOrigin?: string | null
  statusSyncVersion?: number
  readinessScore?: number
  readyChecklistCount?: number
  totalChecklistCount?: number
  readinessUpdatedAt?: unknown
  attachedFormIds?: string[]
  attachedDesignIds?: string[]
  attachedUploadIds?: string[]
  attachedLinkIds?: string[]
  weightage: number
  assignedEditors: string[]
  providerMappings?: ElementProviderMappings
  mapProviderId?: string
  /** Education structure tag; server-written only via tagEduStructure callable. */
  edu?: EduElementTag
}

export type EduNodeKind =
  | 'YEAR'
  | 'CLASS'
  | 'SUBJECT'
  | 'PROGRAMME'
  | 'GROUP'
  | 'CLIENT'
  | 'PHASE'

export type EduTeacherRole = 'CLASS_TEACHER' | 'SUBJECT_TEACHER'
export type EduTeacherGrantType = 'EDIT' | 'VIEW_CLASS'

export interface EduTeacherAssignment {
  role: EduTeacherRole
  grantedByAssignment: EduTeacherGrantType[]
}

export type EduModule = 'teaching' | 'training'

export interface EduElementTag {
  kind: EduNodeKind
  module: EduModule
  archivedYear?: boolean
  rolledOverTo?: string
  teachers?: Record<
    string,
    {
      role: 'CLASS_TEACHER' | 'SUBJECT_TEACHER'
      grantedByAssignment: Array<'EDIT' | 'VIEW_CLASS'>
      name?: string
    }
  >
}

export interface EduRoom {
  id: string
  name: string
  active: boolean
}

export interface EduSettings {
  workspaceId: string
  rooms: EduRoom[]
  absenceAlerts?: boolean
  messagingEnabled?: boolean
  updatedAt?: unknown
  updatedBy?: string
}

export interface EduYearHoliday {
  from: string
  to: string
  label?: string
}

export interface EduYearSettings {
  workspaceId: string
  rootId: string
  holidays: EduYearHoliday[]
  updatedAt?: unknown
  updatedBy?: string
}

export interface EduSessionSeries {
  id?: string
  workspaceId: string
  rootId: string
  classId: string
  nodeId: string
  module: EduModule
  teacherUids: string[]
  weekdays: number[]
  startTime: string
  durationMin: number
  fromDate: string
  toDate: string
  roomId?: string | null
  onlineUrl?: string | null
  readAccess: string[]
  bookable?: boolean
  capacity?: number | null
  createdBy: string
  updatedAt: unknown
}

export type EduSessionStatus = 'PLANNED' | 'CANCELLED'

export type EduAttendanceStatus = 'PRESENT' | 'ABSENT' | 'LATE' | 'ON_LEAVE' | 'EXCUSED'

export type EduAttendanceSource = 'WEB' | 'MEMBER' | 'HELPER' | 'LEAVE'

export interface EduAttendanceLastChange {
  by: string
  at: unknown
  reason?: string
}

export interface EduAttendance {
  id?: string
  workspaceId: string
  sessionId: string // (= sessionKey)
  seriesId: string
  date: string
  nodeId: string
  classId: string
  rootId: string
  learnerId: string
  status: EduAttendanceStatus
  source: EduAttendanceSource // ('WEB' now; MEMBER/HELPER/LEAVE later)
  markedBy: string
  markedAt: unknown
  readAccess: string[]
  teacherUids: string[]
  substituteUid?: string | null
  lastChange?: EduAttendanceLastChange
  leave?: {
    requestId: string
    previousStatus: EduAttendanceStatus | null
  }
}

export type EduLeaveStatus = 'PENDING' | 'APPROVED' | 'DECLINED' | 'CANCELLED' | 'REVOKED'
export type EduLeaveRelation = 'GUARDIAN' | 'SELF'

export interface EduLeaveRequest {
  id?: string
  workspaceId: string
  learnerId: string
  classIds: string[]
  fromDate: string
  toDate: string
  reason: string
  status: EduLeaveStatus
  requestedBy: string
  relation: EduLeaveRelation
  createdAt: unknown
  decidedBy?: string | null
  decidedAt?: unknown | null
  decisionNote?: string | null
  readAccess: string[]
  teacherUids: string[]
}

export type EduSubmissionStatus = 'SUBMITTED' | 'LATE' | 'RETURNED' | 'APPROVED'
export type EduSubmissionSource = 'PAPER' | 'WEB' | 'MEMBER' | 'HELPER'

export interface EduSubmissionHistoryEntry {
  status: EduSubmissionStatus
  by: string
  at: unknown
  note?: string
}

export interface EduSubmission {
  id?: string
  workspaceId: string
  checklistId: string
  nodeId: string
  classId: string
  learnerId: string
  status: EduSubmissionStatus
  submittedAt: unknown
  submittedBy: string
  source: EduSubmissionSource
  note?: string
  attachments: ChecklistAttachment[]
  feedback?: string
  score?: number
  maxScore?: number | null
  reviewedBy?: string
  reviewedAt?: unknown
  history: EduSubmissionHistoryEntry[]
  readAccess: string[]
  teacherUids: string[]
}

export interface EduSession {
  id?: string
  workspaceId: string
  seriesId: string
  nodeId: string
  classId: string
  rootId: string
  date: string
  startTime: string
  durationMin: number
  status: EduSessionStatus
  substituteUid?: string | null
  topicItemIds: string[]
  note?: string | null
  readAccess: string[]
  teacherUids?: string[]
  rollTakenAt?: unknown
  rollTakenBy?: string
  rollCounts?: Record<EduAttendanceStatus, number>
  updatedBy: string
  updatedAt: unknown
}

export interface EduEnrollmentPeriod {
  from: unknown
  to: unknown
}

export interface EduEnrollment {
  id?: string
  workspaceId: string
  learnerId: string
  nodeId: string
  rollNo?: string
  readAccess: string[]
  from: unknown
  to: unknown | null
  endedBy?: string | null
  previousPeriods?: EduEnrollmentPeriod[]
}

export type EduEnrolment = EduEnrollment

// ---------------------------------------------------------------------------
// ED10: Booking, Packs & Memberships
// ---------------------------------------------------------------------------

export type EduBookingStatus =
  | 'BOOKED'
  | 'CANCELLED'
  | 'WAITLIST'
  | 'LATE_CANCEL'
  | 'ATTENDED'
  | 'NO_SHOW'

export interface EduBooking {
  id: string
  workspaceId: string
  seriesId: string
  date: string
  sessionKey: string
  learnerId: string
  entitlementId?: string | null
  dropInDueId?: string | null
  entitlementType?: 'PACK' | 'MEMBERSHIP' | 'DROP_IN' | 'CREDIT' | null
  sessionsRemainingSnapshot?: number | null
  status: EduBookingStatus
  bookedBy: string
  bookedVia: 'FAMILY' | 'STAFF'
  rootId: string
  nodeId: string
  readAccess: string[]
  teacherUids: string[]
  requestId?: string | null
  cancelReason?: string | null
  cancelledAt?: string | null
  cancelledBy?: string | null
  movedTo?: { seriesId: string; date: string; bookingId: string } | null
  movedFrom?: { seriesId: string; date: string; bookingId: string } | null
  waitlistPosition?: number | null
  waitlistJoinedAt?: string | null
  freeCancelOnce?: boolean
  lateCancelNow?: boolean
  promotedAt?: string | null
  promotedBy?: string | null
  lateCancelWaived?: boolean
  lateCancelWaiveReason?: string | null
  lastPromotionSkippedReason?: string | null
  lastPromotionSkippedAt?: string | null
  attendanceMark?: string | null
  attendanceMarkedAt?: string | null
  learnerName?: string
  rollNo?: string | null
  title?: string
  startTime?: string
  createdAt?: unknown
  updatedAt?: unknown
}

export type EduEntitlementKind = 'PACK' | 'MEMBERSHIP' | 'CREDIT'
export type EduEntitlementStatus = 'ACTIVE' | 'NOT_PAID' | 'EXPIRED' | 'ENDED' | 'ASSIGNED'
export type EduEntitlementLedgerAction = 'HOLD' | 'RELEASE' | 'USE' | 'EXPIRE' | 'EXTEND' | 'CREDIT' | 'VOID'

export interface EduEntitlementLedgerEntry {
  id?: string
  action: EduEntitlementLedgerAction
  bookingId?: string | null
  by: string
  reason?: string | null
  at: string
  sessionsDiff?: number
  previousStatus?: EduEntitlementStatus
  newStatus?: EduEntitlementStatus
}

export interface EduEntitlement {
  id: string
  workspaceId: string
  planId: string
  learnerId: string
  kind: EduEntitlementKind
  rootId: string
  nodeIds?: string[]
  sessionsTotal?: number | null
  sessionsHeld?: number
  sessionsUsed?: number
  periodStartDate?: string | null
  periodEndDate?: string | null
  validFrom?: string | null
  validTo?: string | null
  status: EduEntitlementStatus
  capPer?: 'WEEK' | 'MONTH' | null
  capCount?: number | null
  creditOverride?: boolean
  creditReason?: string | null
  creditGrantedBy?: string | null
  creditGrantedAt?: string | null
  freeze?: { from: string; to: string; reason: string; frozenAt: string; frozenBy: string } | null
  expiryRemindersSent?: Record<string, string>
  planTitle?: string
  sessionsRemaining?: number
  frozenUntil?: string | null
  ledger?: EduEntitlementLedgerEntry[]
  history?: unknown[]
  createdAt?: unknown
  updatedAt?: unknown
}

export interface EduBookingSettings {
  bookAheadDays: number
  cancelWindowHours: number
  strikesToBlock: number
  strikeWindowDays: number
  blockDays: number
  usableWhen: 'PAID' | 'ASSIGNED'
  dropIn: {
    enabled: boolean
    priceMinor: number
  }
  membershipCap: { capPer: 'WEEK' | 'MONTH'; capCount: number } | null
}

export type ChecklistItemEduKind = 'LESSON' | 'HOMEWORK' | 'TEST' | 'SESSION_PLAN'

export interface ChecklistItem {
  id: string
  parentContainerId: string
  workspaceId: string
  workspaceSlug?: string
  board?: TimelineBoardKey
  publicId?: string
  routePath?: string
  parentRoutePath?: string
  deletedFromBoard?: TimelineBoardKey | null
  deletedAt?: unknown
  title: string
  boardStatus: BoardStatus | null
  priority: number
  startDate?: string | null
  endDate?: string | null
  recordVersion: number
  statusSyncOrigin?: string | null
  statusSyncVersion?: number
  description?: string
  completionCriteria?: string
  createdById?: string
  createdByName?: string
  createdAt?: unknown
  assigneeId?: string | null
  assigneeName?: string | null
  assigneeHistoryIds?: string[]
  attachedFormIds?: string[]
  attachedDesignIds?: string[]
  attachedUploadIds?: string[]
  attachedLinkIds?: string[]
  updatedAt?: unknown
  /** Optional provider mappings; absent on pre-Wave-2 checklist records. */
  providerMappings?: ElementProviderMappings
  lastUpdatedBy?: string | null
  lastUpdatedByName?: string | null
  isCompleted: boolean
  /** Wave 3 inventory scanner readiness; set when the linked resource is scanned out. */
  isReady?: boolean
  resourceReadySku?: string | null
  resourceReadyAt?: unknown
  weightage: number
  /** Server-computed progress for repeating (DAILY / WEEKLY) checklists */
  periodProgress?: {
    done: number
    due: number
    ratio: number
    computedAt?: unknown
  }
  position: {
    x: number
    y: number
  }
  workspaceCanvasLayoutVersion?: number
  prerequisites: string[]
  /** Cross-type prerequisite ids used by workflow surfaces such as Stripboard. */
  dependencyNodeIds?: string[]
  invalidDependencyNodeIds?: string[]
  blockedByExternal: {
    targetType: CanvasElementType
    targetId: string
  } | null
  blockedByExternalId?: string | null
  interWorkspaceTargetId?: string | null
  dependencyLogicGate?: 'ALL_PREREQUISITES_MET' | 'ANY_PREREQUISITE_MET'
  hasAutoSpawnTrigger?: boolean
  autoSpawnTemplates?: Array<{
    title: string
    weightage: number
    relativeYOffset: number
  }>
  requiresProofFile?: boolean
  proofFileUrl?: string | null
  proofFileName?: string | null
  proofFileStoragePath?: string | null
  cardBackgroundFileId?: string | null
  cardBackgroundUrl?: string | null
  cardBackgroundFileName?: string | null
  cardBackgroundStoragePath?: string | null
  proofSubmissionId?: string | null
  proofUploadedById?: string | null
  proofUploadedByName?: string | null
  proofUploadedAt?: string | null
  requiresCoSign?: boolean
  coSignerRoleTarget?: 'ADMIN' | 'MANAGER' | string
  coSignedByUid?: string | null
  coSignedByName?: string | null
  isPendingReview?: boolean
  /** Proof approval — specific member who must sign off on submitted proof
   * before the item can move to COMPLETED. When null/undefined, proof upload
   * auto-completes the item (legacy fast path). */
  proofApproverId?: string | null
  proofApproverName?: string | null
  /** Lifecycle of the current proof submission. Set to PENDING the moment
   * proof is attached (only when proofApproverId is set); flipped to
   * APPROVED on approval or REJECTED on rejection. Null when no proof or
   * no approver is set. */
  proofReviewStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null
  proofReviewedAt?: string | null
  proofReviewedById?: string | null
  proofReviewedByName?: string | null
  /** Rejection note posted by the approver; also mirrored into
   * checklist_comments for the assignee to see inline. */
  proofRejectionNote?: string | null
  requiresLocationPin?: boolean
  targetCoordinates?: { lat: number; lng: number } | null
  verifiedCoordinates?: { lat: number; lng: number } | null
  proofVideoUrl?: string | null
  memoStoragePath?: string | null
  memoFileName?: string | null
  memoContentType?: string | null
  memoUploadedById?: string | null
  memoUploadedByName?: string | null
  memoUploadedAt?: string | null
  countdownHours?: number | null
  timerStartedAt?: string | null
  timerExpiredAt?: string | null
  isUrgentCrisisMode?: boolean
  enforcePaintSpringDelay?: boolean
  delayDurationHours?: number
  commentsCount?: number
  voiceMemoBlobUrl?: string | null
  isDescriptionPrivate?: boolean
  estimatedHours?: number | null
  actualMinutes?: number | null
  actualCost?: number | null
  complexityStars?: number
  budgetLimit?: number | null
  actualSpend?: number | null
  estimatedCost?: number | null
  soldUnits?: number | null
  // --- Risk & Resource redesign (matrix ratings) ---
  urgencyStars?: number
  importanceStars?: number
  /** ISO 4217 code chosen in the Expense Outlay currency picker. */
  currencyCode?: string
  // --- Calendar-style scheduling ---
  scheduleMode?: ChecklistScheduleMode
  /** "On" (DATED) or "From" (DURATION) date — ISO date string. */
  scheduleStartDate?: string | null
  /** "by" date for DURATION mode — ISO date string. */
  scheduleEndDate?: string | null
  /** Inclusive ISO week keys used by windowed Dashboard checklist queries. */
  searchDateBuckets?: string[]
  /** Whether the time-of-day window is enabled. */
  hasTimeWindow?: boolean
  /** "HH:mm" start of the time window. */
  scheduleTimeFrom?: string | null
  /** "HH:mm" end of the time window. */
  scheduleTimeTo?: string | null
  // --- Checklist-only dependencies (directional, reciprocally mirrored) ---
  blockedByNodeIds?: ChecklistNodeLink[]
  isBlockerForNodeIds?: ChecklistNodeLink[]
  // --- Header workflow status ---
  workflowStatus?: ChecklistWorkflowStatus | null
  /** Status restored when a completed checklist item is reopened. */
  previousWorkflowStatus?: ChecklistWorkflowStatus | null
  // --- Logic-gate node kind (default 'CHECKLIST') ---
  /** When CHECKLIST: a regular task node. When GATE_AND/GATE_OR: a logic gate
   * with 2-4 inputs and 1 output stored in `gateInputIds`. */
  nodeKind?: ChecklistNodeKind
  /** Source checklist node ids feeding into a gate (only meaningful when
   * nodeKind is GATE_AND/GATE_OR). Min 2, max 4 for a fully-connected gate. */
  gateInputIds?: string[]
  /** Phase 2.C — per-item override of the Helper visibility mask. Missing keys
   * fall back to the workspace default; default-allow (see visibilityMask.ts). */
  helperVisibilityMask?: Record<string, boolean>
  /** Wave 1 role gate. Missing means visible to helpers for legacy records. */
  isHelperVisible?: boolean
  scopeRead?: string[]
  scopeWrite?: string[]
  /** Stock module — IDs of workspace_stock items this task requires. Optional
   * and soft-coupled: when absent/empty, all stock UI and lifecycle hooks
   * short-circuit. Source of truth for StockItem.linkedTaskIds. */
  requiredStockIds?: string[]
  /** Quantity-aware replacement for `requiredStockIds`. Existing records are
   * lazily migrated by UI/service readers; `requiredStockIds` remains a
   * compatibility mirror for older paths. */
  stockRequirements?: Array<{
    stockItemId: string
    quantity: number
    unitOverride?: string | null
    required?: boolean
  }>
  /** Stock consumption strategy. Omitted ⇒ 'reserve'. 'consume' decrements
   * quantity on task completion; 'reserve' just blocks reservedQuantity. */
  consumptionMode?: 'reserve' | 'consume'
  /** Optional stock and budget effects applied by later lifecycle integrations. */
  bindings?: ChecklistItemBindings
  /** Server-written record of side effects that fired for this item. Never written from the client. */
  sideEffects?: ChecklistItemSideEffects
  // --- Recurring Checklists (singleton model) ---
  /** MAIN = one-shot; DAILY / WEEKLY = repeats. In the singleton model every
   *  editable field lives here on the master item; only per-occurrence
   *  completion is forked into `occurrences`. */
  checklistType?: 'INDIVIDUAL' | 'DAILY' | 'WEEKLY'
  /** Per-occurrence completion + workflow status. Key = ISO date (DAILY,
   *  "YYYY-MM-DD") or ISO week ("YYYY-Www"). Master `isCompleted` /
   *  `workflowStatus` mirror the CURRENT occurrence (today for DAILY, this
   *  week for WEEKLY) so surfaces that don't know about occurrences still
   *  render sensibly. */
  occurrences?: Record<string, RecurringOccurrenceState>
  /** Soft-archive: hides from active queries; type-swap DAILY|WEEKLY -> MAIN
   *  sets this and stashes the prior type in `preArchiveChecklistType`. */
  isArchived?: boolean
  archivedAt?: string | null
  archivedById?: string | null
  archivedByName?: string | null
  /** Original checklistType before soft-archive so unarchive can restore it. */
  preArchiveChecklistType?: 'INDIVIDUAL' | 'DAILY' | 'WEEKLY' | null
  // Legacy fields kept for read-compat with older docs until purge runs.
  recurrenceTemplateId?: string | null
  recurrenceBlueprintId?: string | null
  occurrenceKey?: string | null
  isRecurrenceInstance?: boolean
  /** Derived, non-persisted. Set by projection layer at read time from
   *  scheduleStartDate/EndDate + workspace todosLeadDays. Never write to
   *  Firestore. Consumed by Todos-feed visibility gates. */
  timeStatus?: ChecklistTimeStatus
  /** Parent-scoped recurring range mirrored from the parent element at
   *  create time (and refreshed by the parent-date-change reconciler).
   *  Recurring canvases filter items by this window on every focused day
   *  so a Daily / Weekly recurring only appears on dates that fall inside
   *  its parent's start/end. */
  recurringWindow?: {
    parentElementId: string
    startDate: string
    endDate: string
    syncedAt?: unknown
  }
  /** Period key (YYYY-MM-DD or YYYY-Www) from which this repeating item is marked not needed. */
  notRequiredFrom?: string | null
  repeatPaused?: boolean
  repeatPausedReason?: string | null
  proofPeriodKey?: string | null
  // --- Education structure & scheduling ---
  eduKind?: ChecklistItemEduKind
  dueAt?: string | null
}

export interface ChecklistItemBindings {
  budgetBinding?: {
    category: string
    spendTiming: 'ON_COMPLETION'
  }
}

/** Server-written record of side effects that fired for this item. Never
 *  written from the client. */
export interface ChecklistItemSideEffects {
  budgetSpends?: Array<{
    ledgerEntryId?: string
    expenseId?: string
    entityRef?: BudgetEntityRef
    amountMinor: number
    appliedAt: string
    triggerVersion: 'v1'
  }>
}

export interface RecurringOccurrenceState {
  isCompleted: boolean
  workflowStatus: ChecklistWorkflowStatus | null
  boardStatus?: BoardStatus | null
  completedAt?: string | null
  completedById?: string | null
  completedByName?: string | null
}

export type ChecklistItemWithDependencyState = ChecklistItem & {
  isLockedByDependency: boolean
}

// ============================================================================
// Advanced Gamification, Retention & Monetization Layer
// ----------------------------------------------------------------------------
// Added by the "Advanced Gamification & Monetization Net" spec.
//
// Wallet state is intentionally excluded. Personal credits live only at
// `users/{uid}/wallet/personal`; the profile is keyed at
// `users/{uid}/gamification/profile`.
// ============================================================================

/** The fourteen "Progressive Visibility" activity tracks (four tiers each). */
export type ActivityTrackId =
  | 'TRACK_CREATE_GOAL'
  | 'TRACK_CREATE_TASK'
  | 'TRACK_CREATE_SUB'
  | 'TRACK_CREATE_CHECK'
  | 'TRACK_FINISH_GOAL'
  | 'TRACK_FINISH_TASK'
  | 'TRACK_FINISH_SUB'
  | 'TRACK_FINISH_CHECK'
  | 'TRACK_AI_SPAWN'
  | 'TRACK_AI_REFINE'
  | 'TRACK_FOCUS_TIME'
  | 'TRACK_LUCKY_SPINS'
  | 'TRACK_MEGA_SPINS'
  | 'TRACK_GIGA_SPINS'

/**
 * Retention ladders. These have more than four breakpoints, so they are driven
 * by the dedicated `cumulativeLoginsCount` / `consecutiveLoginStreak` counters
 * rather than the four-tier `trackProgress` map.
 */
export type RetentionTrackId = 'TRACK_TOTAL_LOGINS' | 'TRACK_STREAK_LOGINS'

export type TrackId = ActivityTrackId | RetentionTrackId

export type SpinTier = 'LUCKY_SPIN' | 'MEGA_SPIN' | 'GIGA_SPIN'

export type CosmeticRarity = 'COMMON' | 'RARE' | 'EPIC' | 'LEGENDARY'

/** Raid-only viewport overlays. Distinct from the global `DashboardViewKey`. */
export type RaidView = 'TIMELINE' | 'CONSTELLATION' | 'WAR_ROOM'

export interface TrackState {
  trackId: string       // Constant matching tracking identifier
  currentLevel: number  // Active tier level clamped from 1 to 4
  currentCount: number  // Raw cumulative count total metrics tracked
}

export interface UserGamificationProfile {
  uid: string
  level: number
  currentXp: number
  xpNeededForNextLevel: number

  // Dynamic Ticket Engine Allocations
  luckySpinTickets: number
  megaSpinTickets: number
  gigaSpinTickets: number

  // Anti-Cheat Resets (saved as ISO strings from cloud-time)
  lastMidnightResetTimestamp: string
  lastActiveLoginTimestamp: string

  // Daily Tracking Cap Accumulators (reset daily at midnight cloud-time)
  dailyCreditsEarnedFromActivity: number
  dailyLuckySpinsExecuted: number
  dailyMegaSpinsExecuted: number
  dailyGigaSpinsExecuted: number

  // Active Retention Milestones
  cumulativeLoginsCount: number
  consecutiveLoginStreak: number

  // Equipped Identity Slots
  equippedAvatarId: string
  equippedTitle: string
  equippedCanvasBgId: string
  equippedThemeId: string
  equippedAudioId: string
  equippedOrbitSkinId?: string

  // Public Profile Showcase Canvas Arrays
  bioMarkdown: string
  pinnedBadgeIds: string[]            // Maximum 4 custom badges pinned to card
  pinnedTimelineElementIds: string[]  // Live project progress bars anchored on Profile

  // Inventories Unlocked Matrix
  unlockedAvatarIds: string[]
  unlockedThemeIds: string[]
  unlockedBackgroundIds: string[]
  unlockedOrbitSkinIds?: string[]
  unlockedTitlePrefixes: string[]
  unlockedTitleSuffixes: string[]

  // Central Progression Ledger Mapping (activity tracks only)
  trackProgress: Record<string, TrackState>

  /**
   * Spin tickets EARNED from track tiers but not yet collected — held per
   * track until the user clicks Collect on that card. The Collect handler
   * drains the bucket into luckySpinTickets / megaSpinTickets / gigaSpinTickets
   * and resets it to {}. Keyed by ActivityTrackId, values keyed by SpinTier.
   */
  unclaimedTrackTickets: Record<string, Partial<Record<SpinTier, number>>>
}

export interface RaidDamageEntry {
  userEmail: string
  userDisplayName: string
  damageDealt: number
}

export interface ActiveRaidModel {
  raidId: string
  targetElementId: string    // Maps directly to the target Goal/Task container node ID
  bossTitle: string
  isPenaltyRaid: boolean     // false = Normal, true = Penalty (Option B failure triggers)
  defaultRaidView: RaidView
  allowedRaidViews: RaidView[]
  initialBossHp: number
  currentBossHp: number
  allocatedCreditPool: number // Credit funds locked out of Editor/Admin wallet
  enrageDeadlineTimestamp: string // End date timestamp tracking countdown
  damageLeaderboard: Record<string, RaidDamageEntry>
}
